import type { AccessDeviceManagementAttempt } from '@/api/core/access-device-management';
import { postAccessDeviceManagement } from '@/api/core/access-device-management';
import { canReadProperties, canWriteProperties } from '@/api/core/authorization';
import { type NativeProperty } from '@/api/core/native-property-read';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import { UnsavedGuard } from '@/components/unsaved-guard';
import {
  Alert,
  AlertDescription,
  Button,
  ConfirmDialog,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useInsertionEffect, useMemo, useRef, useState } from 'react';
import {
  nativePropertiesQuery,
  nativePropertyCatalogQuery,
  type PropertyRuntime,
} from '../property/property-queries';

interface Draft {
  property: NativeProperty;
}
interface ScopedAttempt extends AccessDeviceManagementAttempt {
  readonly apiNamespace: string;
  readonly generation: number;
}
interface FormValues {
  deviceId: string;
  gatewayId: string;
  deviceType: string;
  deviceTypeProvided: boolean;
  deviceName: string;
  deviceNameProvided: boolean;
  gatewayName: string;
  gatewayNameProvided: boolean;
  battery: string;
  isActive: boolean;
}
const initialValues: FormValues = {
  deviceId: '',
  gatewayId: '',
  deviceType: '',
  deviceTypeProvided: false,
  deviceName: '',
  deviceNameProvided: false,
  gatewayName: '',
  gatewayNameProvided: false,
  battery: '',
  isActive: true,
};

function errorMessage(error: unknown): string {
  if (isApiError(error)) {
    if (error.status === 400) return '입력값을 확인해 주세요. 입력한 내용은 보존했습니다.';
    if (error.status === 403) return '현재 지점 등록 권한을 확인할 수 없습니다.';
    if (error.status === 404)
      return '선택한 지점 범위를 확인할 수 없습니다. 지점과 환경을 다시 선택해 주세요.';
    if (error.status === 409)
      return '등록 정보가 충돌했습니다. 초안을 보존했습니다. 최신 상태를 확인한 뒤 새 초안으로 다시 결정해 주세요.';
    if (
      error.kind === 'network' ||
      error.kind === 'timeout' ||
      (error.status !== undefined && error.status >= 500)
    )
      return '요청 결과를 확인하지 못했습니다. 같은 요청을 자동으로 다시 보내지 않았습니다.';
  }
  return '등록 요청을 확인하지 못했습니다. 입력한 내용은 보존했습니다.';
}

export function AccessDeviceCreateForm({
  runtime,
  generation,
  onClose,
}: {
  runtime: PropertyRuntime;
  generation: number;
  onClose: () => void;
}) {
  const session = useOperatorSession(runtime.session);
  const queryClient = useQueryClient();
  const [cursor, setCursor] = useState<string | null>(null);
  const [propertyId, setPropertyId] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [values, setValues] = useState(initialValues);
  const [attempt, setAttempt] = useState<ScopedAttempt | null>(null);
  const [pending, setPending] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState<string>();
  const [receipt, setReceipt] = useState<string>();
  const [savedRevision, setSavedRevision] = useState<number>();
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'close' | 'abandon'>('close');
  const flight = useRef<object | null>(null);
  const controller = useRef<AbortController | null>(null);
  const apiNamespace = runtime.apiNamespace;
  const activeScope = useRef({ apiNamespace, generation, propertyId });
  useInsertionEffect(() => {
    activeScope.current = { apiNamespace, generation, propertyId };
  }, [apiNamespace, generation, propertyId]);
  const canRead =
    session.status === 'authorized' &&
    session.generation === generation &&
    !session.deniedDomains.includes('property') &&
    !!session.authorization &&
    canReadProperties(session.authorization);
  const properties = useQuery(nativePropertiesQuery(runtime, generation, cursor, canRead));
  const selected = properties.data?.data.find((item) => item.id === propertyId);
  const catalog = useQuery(
    nativePropertyCatalogQuery(
      runtime,
      generation,
      propertyId,
      canRead && Boolean(propertyId),
      () =>
        activeScope.current.propertyId === propertyId &&
        activeScope.current.apiNamespace === apiNamespace,
    ),
  );
  const canWrite =
    session.status === 'authorized' &&
    session.generation === generation &&
    !session.deniedDomains.includes('property') &&
    Boolean(session.authorization && canWriteProperties(session.authorization));
  const verifiedProperty = useMemo(() => {
    const item = catalog.data?.data.property;
    return selected && item?.id === selected.id && item.id === propertyId ? selected : undefined;
  }, [catalog.data, propertyId, selected]);

  useEffect(
    () => () => {
      controller.current?.abort();
    },
    [],
  );

  const resetDraft = () => {
    controller.current?.abort();
    controller.current = null;
    setDraft(null);
    setAttempt(null);
    setValues(initialValues);
    setUncertain(false);
    setError(undefined);
    setReceipt(undefined);
    setSavedRevision(undefined);
  };
  const selectProperty = (value: string | null) => {
    if (value === null) return;
    setPropertyId(value);
    resetDraft();
    if (!value) return;
    const item = properties.data?.data.find((row) => row.id === value);
    if (item) setDraft({ property: item });
  };

  const buildBody = () => {
    if (
      !draft ||
      !verifiedProperty ||
      Array.from(values.deviceId).length < 1 ||
      Array.from(values.deviceId).length > 50 ||
      Array.from(values.gatewayId).length < 1 ||
      Array.from(values.gatewayId).length > 50 ||
      values.deviceId.split('').some((char) => {
        const code = char.codePointAt(0) ?? 0;
        return code <= 31 || (code >= 127 && code <= 159);
      }) ||
      values.gatewayId.split('').some((char) => {
        const code = char.codePointAt(0) ?? 0;
        return code <= 31 || (code >= 127 && code <= 159);
      })
    )
      return null;
    if (values.deviceTypeProvided && Array.from(values.deviceType).length > 24) return null;
    if (
      (values.deviceNameProvided && Array.from(values.deviceName).length > 50) ||
      (values.gatewayNameProvided && Array.from(values.gatewayName).length > 50)
    )
      return null;
    const battery =
      values.battery === ''
        ? null
        : /^[+-]?\d+$/.test(values.battery)
          ? Number(values.battery)
          : Number.NaN;
    if (
      battery !== null &&
      (!Number.isInteger(battery) || battery < -(2 ** 31) || battery > 2 ** 31 - 1)
    )
      return null;
    return Object.freeze({
      command_id: crypto.randomUUID(),
      device_id: values.deviceId,
      gateway_id: values.gatewayId,
      is_active: values.isActive,
      slot_ids: Object.freeze([]),
      device_type: values.deviceTypeProvided ? values.deviceType : null,
      retired: false,
      device_name: values.deviceNameProvided ? values.deviceName : null,
      gateway_name: values.gatewayNameProvided ? values.gatewayName : null,
      battery,
    });
  };

  const submit = async (sameAttempt = false) => {
    if (flight.current !== null || !canWrite || !draft || !verifiedProperty) return;
    let captured: ScopedAttempt | null = attempt;
    if (!sameAttempt) {
      if (uncertain || attempt) return;
      const body = buildBody();
      if (!body) {
        setError(
          '필수 ID와 선택한 항목을 확인해 주세요. ID는 1~50자, 장비 유형은 24자, 이름은 50자 이내이며 배터리는 32비트 정수입니다.',
        );
        return;
      }
      captured = Object.freeze({
        property_id: draft.property.id,
        core_device_id: null,
        body,
        apiNamespace,
        generation,
      });
      setAttempt(captured);
    }
    if (!captured) return;
    const currentFlight = {};
    flight.current = currentFlight;
    setPending(true);
    setError(undefined);
    const abort = new AbortController();
    controller.current = abort;
    const isCurrent = () => {
      const current = runtime.session.getSnapshot();
      return (
        !abort.signal.aborted &&
        current.status === 'authorized' &&
        current.generation === captured.generation &&
        runtime.apiNamespace === captured.apiNamespace &&
        activeScope.current.apiNamespace === captured.apiNamespace &&
        activeScope.current.generation === captured.generation &&
        activeScope.current.propertyId === captured.property_id
      );
    };
    try {
      const saved = await postAccessDeviceManagement(
        runtime.client,
        captured.property_id,
        captured.body,
        abort.signal,
      );
      if (!isCurrent()) return;
      setUncertain(false);
      setAttempt(null);
      setDraft(null);
      setPropertyId('');
      setValues(initialValues);
      setReceipt(
        `등록 정보 저장됨. Core 장비 UUID ${saved.core_device_id}, revision ${String(saved.revision)}. 물리적 작동 상태는 확인하지 않았습니다.`,
      );
      setSavedRevision(saved.revision);
      const rootPrefix = ['core', runtime.apiNamespace, generation, 'access-device-registry'];
      const propertyPrefix = [
        'core',
        runtime.apiNamespace,
        generation,
        'core-access-device-inventory',
        captured.property_id,
      ];
      const catalogPrefix = [
        'core',
        runtime.apiNamespace,
        generation,
        'native-property-catalog',
        captured.property_id,
      ];
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: rootPrefix, refetchType: 'active' }),
        queryClient.invalidateQueries({ queryKey: propertyPrefix, refetchType: 'active' }),
        queryClient.invalidateQueries({
          queryKey: [
            'core',
            apiNamespace,
            generation,
            'native-access-inventory',
            captured.property_id,
          ],
          refetchType: 'active',
        }),
        queryClient.invalidateQueries({ queryKey: catalogPrefix, refetchType: 'active' }),
      ]);
    } catch (failure) {
      if (!isCurrent()) return;
      if (isApiError(failure) && failure.status === 403) {
        runtime.session.reportDomainAccessDenied('property', generation);
      }
      if (isApiError(failure) && failure.status === 400) setAttempt(null);
      if (
        isApiError(failure) &&
        (failure.kind === 'network' ||
          failure.kind === 'timeout' ||
          failure.kind === 'invalid-response' ||
          (failure.status !== undefined && failure.status >= 500))
      )
        setUncertain(true);
      setError(errorMessage(failure));
    } finally {
      if (flight.current === currentFlight) {
        flight.current = null;
        setPending(false);
        if (controller.current === abort) controller.current = null;
      }
    }
  };

  const hasDraftContent =
    Boolean(draft) ||
    values.deviceId !== '' ||
    values.gatewayId !== '' ||
    values.deviceTypeProvided ||
    values.deviceNameProvided ||
    values.gatewayNameProvided ||
    values.battery !== '' ||
    attempt !== null ||
    uncertain;
  const guardRouteExit = canWrite && (pending || uncertain);
  const closeForm = () => {
    resetDraft();
    setPropertyId('');
    onClose();
  };

  return (
    <section aria-label="새 장비 등록" className="space-y-4 rounded-md border p-4 sm:p-5">
      <UnsavedGuard
        when={guardRouteExit}
        suppressConfirmation={closeConfirmOpen}
        autoProceedWhenClean={false}
        warning={{
          target: '진행 중이거나 결과가 불확실한 등록 요청',
          action: '요청 내용을 버리고 이동',
          consequence:
            '앞선 등록 요청은 이미 저장되었을 수 있습니다. 진행 중인 요청은 화면을 떠난 뒤에도 서버에서 처리될 수 있고, 화면을 떠나도 이미 저장된 등록은 되돌릴 수 없습니다. 이탈하면 원래 식별자와 요청 본문을 잃어 같은 요청을 수동으로 다시 확인할 수 없습니다.',
        }}
      />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="title-sm">새 장비 등록</h2>
          <p className="body-sm text-muted-foreground mt-1">
            선택한 지점에 연결 없이 등록 정보를 저장합니다.
          </p>
        </div>
        <Button
          variant="outline"
          disabled={pending}
          onClick={() => {
            if (hasDraftContent) {
              setConfirmAction('close');
              setCloseConfirmOpen(true);
            } else closeForm();
          }}
        >
          닫기
        </Button>
      </div>
      <FormField
        label="대상 지점"
        htmlFor="new-device-property"
        message="현재 환경의 지점을 목록에서 직접 선택하세요."
      >
        <Select
          value={propertyId}
          disabled={!canRead || properties.isPending || pending || uncertain || draft !== null}
          onValueChange={selectProperty}
        >
          <SelectTrigger
            id="new-device-property"
            aria-label="대상 지점"
            className="min-h-11 w-full"
          >
            <SelectValue placeholder="지점을 선택하세요">
              {(value) => {
                const item = properties.data?.data.find((row) => row.id === value);
                return item ? `${item.name} · ${item.code}` : undefined;
              }}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {(properties.data?.data ?? []).map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.name} · {item.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {properties.isPending && <p role="status">지점 목록을 불러오고 있습니다.</p>}
        {properties.isError && (
          <p role="alert" className="caption text-destructive mt-1">
            지점 목록을 불러오지 못했습니다.
          </p>
        )}
        {properties.data?.next_cursor && (
          <Button
            className="mt-2"
            variant="outline"
            disabled={properties.isFetching || pending || Boolean(propertyId)}
            onClick={() => {
              setCursor(properties.data.next_cursor);
            }}
          >
            다음 지점 50개
          </Button>
        )}
      </FormField>
      {draft && !attempt && (
        <Button
          variant="outline"
          disabled={pending}
          onClick={() => {
            setConfirmAction('abandon');
            setCloseConfirmOpen(true);
          }}
        >
          지점 선택 다시 하기
        </Button>
      )}
      {propertyId && (catalog.isPending || !verifiedProperty) && (
        <p role="status" className="body-sm text-muted-foreground">
          선택한 지점의 현재 목록과 환경을 확인하고 있습니다.
        </p>
      )}
      {catalog.isError && propertyId && (
        <Alert variant="warning">
          <AlertDescription>
            선택한 지점 범위를 확인하지 못했습니다. 지점 목록을 다시 확인한 뒤 시도해 주세요.
          </AlertDescription>
          <Button
            variant="outline"
            disabled={catalog.isFetching}
            onClick={() => void catalog.refetch()}
          >
            지점 정보 다시 조회
          </Button>
        </Alert>
      )}
      {verifiedProperty && draft && (
        <>
          <p className="body-sm">
            선택한 지점: {verifiedProperty.name} · {verifiedProperty.code}
          </p>
          <FormField
            label="장비 ID"
            htmlFor="new-device-id"
            message="원문 그대로 저장 · 선행 0 포함 · 최대 50 코드 포인트"
          >
            <Input
              id="new-device-id"
              value={values.deviceId}
              maxLength={100}
              disabled={pending || uncertain}
              onChange={(event) => {
                setValues({ ...values, deviceId: event.target.value });
              }}
            />
          </FormField>
          <FormField
            label="게이트웨이 ID"
            htmlFor="new-gateway-id"
            message="원문 그대로 저장 · 선행 0 포함 · 최대 50 코드 포인트"
          >
            <Input
              id="new-gateway-id"
              value={values.gatewayId}
              maxLength={100}
              disabled={pending || uncertain}
              onChange={(event) => {
                setValues({ ...values, gatewayId: event.target.value });
              }}
            />
          </FormField>
          <div className="grid gap-4 sm:grid-cols-2">
            <NullableTextField
              label="장비 유형"
              id="new-device-type"
              value={values.deviceType}
              provided={values.deviceTypeProvided}
              max={24}
              disabled={pending || uncertain}
              onChange={(deviceType, deviceTypeProvided) => {
                setValues({ ...values, deviceType, deviceTypeProvided });
              }}
            />
            <NullableTextField
              label="장비 이름"
              id="new-device-name"
              value={values.deviceName}
              provided={values.deviceNameProvided}
              max={50}
              disabled={pending || uncertain}
              onChange={(deviceName, deviceNameProvided) => {
                setValues({ ...values, deviceName, deviceNameProvided });
              }}
            />
            <NullableTextField
              label="게이트웨이 이름"
              id="new-gateway-name"
              value={values.gatewayName}
              provided={values.gatewayNameProvided}
              max={50}
              disabled={pending || uncertain}
              onChange={(gatewayName, gatewayNameProvided) => {
                setValues({ ...values, gatewayName, gatewayNameProvided });
              }}
            />
          </div>
          <FormField label="저장된 활성 설정">
            <label className="flex min-h-11 items-center gap-2">
              <input
                type="checkbox"
                checked={values.isActive}
                disabled={pending || uncertain}
                onChange={(event) => {
                  setValues({ ...values, isActive: event.target.checked });
                }}
              />
              활성으로 저장
            </label>
            <p className="caption text-muted-foreground">
              실제 장비 연결이나 작동 상태를 뜻하지 않습니다.
            </p>
          </FormField>
          <FormField
            label="배터리 관측값"
            htmlFor="new-device-battery"
            message="저장된 관측값 · 비워 두면 미제공"
          >
            <Input
              id="new-device-battery"
              inputMode="numeric"
              value={values.battery}
              disabled={pending || uncertain}
              onChange={(event) => {
                setValues({ ...values, battery: event.target.value });
              }}
            />
            <p className="caption text-muted-foreground">
              현재 배터리 잔량을 조회하는 기능은 아닙니다.
            </p>
          </FormField>
          <div className="bg-muted/20 rounded-md border p-3">
            <p className="body-strong">초기 연결 공간</p>
            <p className="body-sm text-muted-foreground mt-1">
              연결 없이 등록합니다. 등록 뒤 공간 연결은 이 화면에서 변경할 수 없습니다.
            </p>
          </div>
          <details className="caption">
            <summary className="cursor-pointer">기술 정보</summary>
            <p className="mt-2">Core 장비 UUID는 저장 응답에서 확인합니다.</p>
          </details>
        </>
      )}
      {error && (
        <Alert variant="warning">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {uncertain && (
        <div className="flex flex-wrap gap-2">
          <Button disabled={pending || !canWrite} onClick={() => void submit(true)}>
            같은 요청 다시 확인/재시도
          </Button>
          <Button
            variant="outline"
            disabled={pending}
            onClick={() => {
              setConfirmAction('abandon');
              setCloseConfirmOpen(true);
            }}
          >
            불확실한 요청을 새 초안에서 분리
          </Button>
        </div>
      )}
      {attempt && !uncertain && !pending && (
        <Button
          variant="outline"
          onClick={() => {
            resetDraft();
            setPropertyId('');
          }}
        >
          응답을 확인하고 새 초안 시작
        </Button>
      )}
      {!attempt && (
        <Button
          tone="brand"
          disabled={!canWrite || !verifiedProperty || !draft || pending || uncertain}
          onClick={() => void submit(false)}
        >
          {pending ? '저장 중' : '등록 정보 저장'}
        </Button>
      )}
      {receipt && (
        <p role="status" className="body-sm text-foreground">
          {receipt}
        </p>
      )}
      {savedRevision !== undefined && (
        <details className="caption">
          <summary className="cursor-pointer">저장된 응답 정보</summary>
          <p className="mt-2">등록 revision {savedRevision}</p>
        </details>
      )}
      {session.status === 'authorized' && !canWrite && (
        <p className="body-sm text-muted-foreground">
          현재 지점 등록 권한이 없어 저장할 수 없습니다.
        </p>
      )}
      <ConfirmDialog
        open={closeConfirmOpen}
        onOpenChange={setCloseConfirmOpen}
        target="새 장비 등록 초안"
        action={uncertain ? '불확실한 요청과 초안 폐기' : '초안 폐기'}
        consequence={
          uncertain
            ? '앞선 등록 요청은 이미 저장되었을 수 있습니다. 초안을 폐기해도 이미 저장된 등록은 되돌릴 수 없고, 원래 식별자와 요청 내용으로 다시 확인할 수 없게 됩니다.'
            : '입력한 값과 생성된 등록 식별자를 버립니다.'
        }
        onConfirm={() => {
          setCloseConfirmOpen(false);
          if (confirmAction === 'abandon') {
            resetDraft();
            setPropertyId('');
          } else closeForm();
        }}
      />
    </section>
  );
}

function NullableTextField({
  label,
  id,
  value,
  provided,
  max,
  disabled,
  onChange,
}: {
  label: string;
  id: string;
  value: string;
  provided: boolean;
  max: number;
  disabled: boolean;
  onChange: (value: string, provided: boolean) => void;
}) {
  return (
    <FormField
      label={label}
      htmlFor={id}
      message={
        provided
          ? `값 지정 · ${String(Array.from(value).length)} / ${String(max)} 코드 포인트`
          : '미제공'
      }
    >
      <label className="caption mb-1 flex items-center gap-2">
        <input
          type="checkbox"
          checked={provided}
          disabled={disabled}
          onChange={(event) => {
            onChange(value, event.target.checked);
          }}
        />
        값 지정
      </label>
      <Input
        id={id}
        value={value}
        disabled={disabled || !provided}
        onChange={(event) => {
          onChange(event.target.value, provided);
        }}
      />
    </FormField>
  );
}
